<template>
    <div>
        <header
            id="global-header-container"
            :aa-region="aaRegion"
            :class="headerClass"
            ref="globalHeader"
        >
            <template v-if="!minimal">
                <section v-if="alert" id="programming-alerts">
                    <div class="programming-alerts-scroll-left-box">
                        <div
                            id="programming-alerts-scroll-left-box-text-id"
                            class="programming-alerts-scroll-left-box-text"
                        >
                            <div class="icon info"></div>
                            {{ alert }}
                        </div>
                    </div>
                </section>

                <div
                    class="header__wrap padded-container"
                    :class="wrapperClass"
                >
                    <nav class="header__nav">
                        <span
                            v-if="isShowPickerRoute"
                            :class="logoClassList"
                        ></span>
                        <a
                            v-else
                            :href="baseURL"
                            :class="logoClassList"
                            :aria-label="getSiteName"
                            :aa-link="
                                [aaRegion, '', 'cbs logo', activityMapBrandLogo]
                                    | aaLinkFilter
                            "
                        >
                        </a>
                    </nav>

                    <ul
                        :class="{
                            header__account: true,
                            'header__nav--items': true
                        }"
                        v-if="!accountFeaturesDisabled"
                    >
                        <template v-if="isLoggedIn">
                            <li
                                v-if="displayAccountMenu"
                                class="header__nav--withsubMenu"
                            >
                                <a
                                    :class="accountMenuLinkClassList"
                                    :aa-link="
                                        [aaRegion, 'user', 'my account']
                                            | aaLinkFilter
                                    "
                                >
                                    {{ displayName }}
                                </a>

                                <ul class="header__subnav content">
                                    <li>
                                        <a
                                            :href="pageAttr.ap_help_faq_url"
                                            target="_blank"
                                            rel="noopener"
                                            data-test="help"
                                            :aa-link="[
                                                aaRegion,
                                                'user',
                                                'help'
                                            ]"
                                        >
                                            {{ $getLocale('help', {}) }}
                                        </a>
                                    </li>
                                    <li>
                                        <a
                                            class="js-user-logout"
                                            @click="logout"
                                            data-test="signout"
                                            :aa-link="
                                                [aaRegion, 'user', 'sign out']
                                                    | aaLinkFilter
                                            "
                                        >
                                            {{ $getLocale('sign_out', {}) }}
                                        </a>
                                    </li>
                                </ul>
                            </li>
                            <li v-else-if="isShowPickerRoute">
                                <span>{{ displayName }}</span>
                            </li>
                        </template>

                        <li v-else-if="displaySignUpLink">
                            <!-- Must be directly after v-if -->
                            <a
                                class="qt-signup"
                                tabindex="0"
                                role="button"
                                :aa-link="
                                    [aaRegion, '', signUpCTACopy.toLowerCase()]
                                        | aaLinkFilter
                                "
                                @click.stop.prevent="onClickSignUp"
                            >
                                {{ $getLocale('sign_up', {}) }}
                            </a>
                        </li>

                        <li v-else-if="displaySignInLink">
                            <div onclick="this.onClickSignIn">
                                <router-link
                                    :to="signInPath"
                                    tabindex="0"
                                    class="qt-signin"
                                    role="button"
                                    :aa-link="
                                        [aaRegion, '', 'sign in'] | aaLinkFilter
                                    "
                                >
                                    {{ $getLocale('sign_in', {}) }}
                                </router-link>
                            </div>
                        </li>
                    </ul>
                </div>
            </template>
            <template v-else>
                <div
                    class="header__wrap header__nav padded-container"
                    :class="wrapperClass"
                >
                    <h1 :class="logoClassList">
                        {{ $getLocale('cbs_all_access_logo', {}) }}
                    </h1>
                </div>
            </template>
            <NotificationBelowHeader></NotificationBelowHeader>
        </header>
        <NotificationBelowHeaderSpacer></NotificationBelowHeaderSpacer>
    </div>
</template>

<script>
    import { mapState, mapGetters } from 'vuex';
    import ButtonCta from 'atoms/ButtonCta';
    import ButtonConfig from 'models/ButtonConfig';
    import Domain from 'helpers/Domain';
    import UserMenu from 'components/userMenu';
    import NotificationBelowHeader from 'aa/vue/components/NotificationBelowHeader';
    import NotificationBelowHeaderSpacer from 'aa/vue/components/NotificationBelowHeaderSpacer';
    import { AA_PURCHASE } from 'helpers/featureConstants';
    import {
        AA_BASE_PATH,
        RENDEZVOUS_BASE_PATH,
        TVE_RENDEZVOUS_BASE_PATH,
        SIGNUP_URL,
        SIGNIN_URL
    } from 'aa/vue/constants/aaRoutes';
    import {
        ACTION_NAMES,
        tracking,
        USER_REG_TYPE
    } from 'aa/vue/services/Tracking';

    let lastScrollY = 40;

    export default {
        name: 'svod-header',

        components: {
            ButtonCta,
            NotificationBelowHeader,
            NotificationBelowHeaderSpacer
        },

        props: {
            aaRegion: {
                type: String
            },
            logoClass: {
                type: String,
                required: true,
                default: ''
            },
            accountFeaturesDisabled: {
                type: Boolean,
                default: false
            },
            minimal: {
                type: Boolean,
                default: false
            },
            showSignIn: {
                type: Boolean,
                default: true
            }
        },

        data: function () {
            return {
                headerClass: [],
                wrapperClass: [],
                alert: null,
                currentRouteName: this.$router.currentRoute.name,
                buttonConfig: new ButtonConfig(),
                baseURL: this.$root.serverData.regionBaseUrl + '/'
            };
        },

        computed: {
            accountMenuLinkClassList() {
                return ['filter', this.isPaymentPage ? 'mpayment' : ''];
            },

            isShowPickerRoute() {
                return this.currentRouteName === 'ShowsPicker';
            },

            isPurchaseFunnelRoute() {
                return (
                    this.$router.options.base ===
                    this.$root.serverData.regionBaseUrl + AA_BASE_PATH
                );
            },

            isRendezvousRoute() {
                return (
                    this.$router.options.base ===
                    this.$root.serverData.regionBaseUrl + RENDEZVOUS_BASE_PATH
                );
            },

            logoClassList() {
                return [
                    'icon',
                    this.logoClass,
                    this.useAllAccessLogo() ? 'siteLogo' : 'cbsLogoBG',
                    'qt-cbslogo'
                ];
            },

            logoUrl: function () {
                return this.isNative
                    ? ''
                    : this.$root.serverData[
                          Domain.isDomestic() ? 'aaLinkFilter' : 'regionBaseUrl'
                      ] + '/';
            },

            isSignInPage() {
                return this.currentRouteName === 'SIGNIN';
            },

            isPaymentPage() {
                return this.currentRouteName === 'PAYMENT';
            },

            isTVERendezvous() {
                return this.currentRouteName === TVE_RENDEZVOUS_BASE_PATH;
            },

            signInPath() {
                let { regionBaseUrl } = this.$root.serverData;

                if (this.isRendezvousRoute) {
                    return `/${this.flowType}/signin/`;
                } else if (this.$store.getters['flow/isReseller']) {
                    return `/signin/${window.location.search}`;
                }

                return `${regionBaseUrl}/signin/`;
            },

            displayAccountMenu() {
                return this.isLoggedIn;
            },

            displaySignInLink() {
                if (Domain.isDomestic()) {
                    return (
                        !this.isLoggedIn &&
                        !this.isSignInPage &&
                        !Boolean(
                            this.$route.meta && this.$route.meta.hideSignInLink
                        )
                    );
                } else {
                    return (
                        !this.isLoggedIn &&
                        !this.isSignInPage &&
                        this.currentRouteName !== 'EDU' &&
                        !this.isTVERendezvous &&
                        this.showSignIn
                    );
                }
            },

            displaySignUpLink() {
                if (Domain.isDomestic()) {
                    return (
                        this.isSignInPage &&
                        !Boolean(
                            this.$route.meta && this.$route.meta.hideSignUpLink
                        )
                    );
                } else {
                    return (
                        this.isSignInPage &&
                        !this.isTVERendezvous &&
                        this.$root.serverData.modeINTL !== true
                    );
                }
            },

            signUpCTACopy() {
                return CBS.Registry.brand.name == 'PPLUS'
                    ? 'Sign Up'
                    : 'Create Account';
            },
            getSiteName() {
                return CBS.Registry.brand.name === 'PPLUS'
                    ? 'Paramount+ Home'
                    : 'CBS Home';
            },
            activityMapBrandLogo() {
                return CBS.Registry.brand.name == 'PPLUS'
                    ? 'paramountplus logo'
                    : 'cbs-digital logo';
            },

            ...mapState('user', {
                displayName: (state) => state.displayName,
                isLoggedIn: (state) => state.isLoggedIn
            }),

            ...mapState({
                authSuiteLoaded: (state) => state['authSuite']?.isLoaded,
                authorizationService: (state) =>
                    state['authSuite']?.authSuiteServices.authorizationService
            }),

            ...mapState('pageAttributes', {
                pageAttr: (state) => state.attributes
            }),

            ...mapState('notification', {
                isClosing: (state) => state.isClosing,
                spacerHeight: (state) => state.spacerHeight
            }),

            ...mapGetters('flow', {
                flowType: 'type',
                isRendezvousFlow: 'isRendezvous',
                isSignInFlow: 'isSignInFlow'
            })
        },

        watch: {
            $route(to, from) {
                this.currentRouteName = to.name;
            }
        },

        filters: {
            //TODO: should be a stand alone filter we import
            //TODO: These will be coming from PageAttributes (is that right?)
            aaLinkFilter: function (val) {
                if (Array.isArray(val)) {
                    while (val.length < 10) {
                        val.push('');
                    }
                    return val.join('|');
                } else if (typeof val === 'string') {
                    return val;
                } else {
                    return null;
                }
            }
        },

        methods: {
            useAllAccessLogo() {
                return !this.isTVERendezvous;
            },

            fadeMenu: function () {
                this.wrapperClass = ['fade-in'];
                window.setTimeout(() => {
                    this.wrapperClass = [];
                }, 2500);
            },

            onScroll() {
                let scrollY = window.scrollY;

                if (scrollY > lastScrollY) {
                    this.onScrollDown();
                } else {
                    this.onScrollUp();
                }

                lastScrollY = scrollY;
            },

            onScrollDown() {
                let scrollY = window.scrollY;

                if (scrollY < 1) {
                    return;
                }

                let classList = this.alert ? ['goUpWithAlert'] : ['goUp'];

                this.headerClass = classList;
            },
            logout() {
                let userMenu = new UserMenu();
                if (this.authSuiteLoaded) {
                    userMenu.logoutWithAuthSuite(this.authorizationService);
                } else {
                    userMenu.logout();
                }
            },

            onScrollUp() {
                let classList = [];
                this.headerClass = classList;
            },

            getSignUpCTACopy() {
                return CBS.Registry.brand.name === 'PPLUS'
                    ? 'Sign Up'
                    : 'Create Account';
            },

            onClickSignIn(e) {
                tracking.trackAction(
                    ACTION_NAMES.LOGIN,
                    {
                        ctaText: "Sign In",
                        menuItemLabel: 'sign in',
                        menuOrientation: 'header',
                        posRowNum: '0',
                        posColNum: '1'

                    }
                );
                window.location.assign(e.target.href);
            },

            onClickSignUp(e) {
                let { regionBaseUrl } = this.$root.serverData;
                let signUpCTA = this.getSignUpCTACopy();
                tracking.trackAction(
                    ACTION_NAMES.SIGN_UP_NON_NATIVE_RENDEZVOUS,
                    { 
                        ctaText: signUpCTA,
                        menuItemLabel: 'sign up',
                        menuOrientation: 'header',
                        posRowNum: '0',
                        posColNum: '1'

                    }
                );

                if (this.isSignInFlow) {
                    let featurePurchase =
                        this.$store.getters.featureIsActive(AA_PURCHASE);

                    if (featurePurchase) {
                        window.location.assign(
                            `${regionBaseUrl + AA_BASE_PATH}user-flow/f-upsell/`
                        );
                    } else {
                        this.$router.push({ name: 'sign_up_explainer' });
                    }
                } else if (this.isPurchaseFunnelRoute) {
                    this.$router.push({ name: 'SIGNUP' });
                } else if (this.$store.getters['flow/isReseller']) {
                    this.$router.push({
                        name: 'SIGNUP',
                        query: this.$route.query
                    });
                } else {
                    // from rendezvous app
                    window.location.assign(`${regionBaseUrl}${SIGNUP_URL}`);
                }
            },
        },

        // Lifecycle Hooks:

        created() {
            window.addEventListener('scroll', this.onScroll);
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll);
        }
    };
</script>
